import { forwardRef } from "react";
import type { FieldValues } from "react-hook-form";
import { NumericFormat } from "react-number-format";

import InputAdornment from "@mui/material/InputAdornment";

import FormTextField from "../FormTextField";

import type { FormTextFieldProps } from "../FormTextField";

interface PercentageFormatInputProps {
  name: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
  value?: number | string | null;
}

// MUI passes the input ref via React's `ref`, so this must forward it.
const FormPercentageInput = forwardRef<
  HTMLInputElement,
  PercentageFormatInputProps
>(function FormPercentageInput({ name, onChange, value, ...other }, ref) {
  const numericValue = value === "" || value == null ? null : Number(value);
  const percentageValue =
    numericValue == null || !Number.isFinite(numericValue)
      ? ""
      : numericValue * 100;

  return (
    <NumericFormat
      {...other}
      name={name}
      allowNegative={false}
      decimalScale={4}
      getInputRef={ref}
      value={percentageValue}
      valueIsNumericString
      onValueChange={({ value: displayedValue }) => {
        onChange({
          target: {
            name,
            value:
              displayedValue === "" ? "" : String(Number(displayedValue) / 100),
          },
        });
      }}
    />
  );
});

export type FormPercentageFieldProps<TValues extends FieldValues> = Omit<
  FormTextFieldProps<TValues>,
  "endAdornment" | "InputProps" | "nullOnEmpty" | "type" | "valueParser"
> & {
  nullOnEmpty?: boolean;
};

/**
 * Displays a percentage while storing its decimal representation in the form.
 * For example, entering 5 stores 0.05 in react-hook-form.
 */
export const FormPercentageField = <TValues extends FieldValues>({
  nullOnEmpty = false,
  ...props
}: FormPercentageFieldProps<TValues>) => (
  <FormTextField
    {...props}
    endAdornment={<InputAdornment position="end">%</InputAdornment>}
    InputProps={{
      inputComponent: FormPercentageInput as never,
      inputProps: { inputMode: "decimal" },
    }}
    valueParser={(value) =>
      value === "" ? (nullOnEmpty ? null : "") : Number(value)
    }
  />
);

export default FormPercentageField;
